import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Download, Printer } from "lucide-react";
import { SectionHeader, Stat } from "@/components/ui-bits";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { getFinancialReport, listTattoos, financialCsvUrl } from "@/lib/api";
import { formatBRL, formatDate, monthName, paymentLabel, statusLabel, statusBadge } from "@/lib/format";

export default function Financial() {
  const currentYear = new Date().getFullYear();
  const currentMonth = String(new Date().getMonth() + 1).padStart(2, "0");
  const [year, setYear] = useState(currentYear);
  const [report, setReport] = useState(null);
  const [monthKey, setMonthKey] = useState(`${currentYear}-${currentMonth}`);
  const [monthTattoos, setMonthTattoos] = useState([]);

  useEffect(() => {
    getFinancialReport(year).then(setReport).catch(() => setReport(null));
  }, [year]);

  useEffect(() => {
    listTattoos({ month: monthKey }).then(setMonthTattoos).catch(() => setMonthTattoos([]));
  }, [monthKey]);

  const chartData = useMemo(
    () => (report?.months || []).map((m) => ({ name: monthName(m.month_num), total: m.total })),
    [report]
  );
  const selectedMonth = useMemo(
    () => report?.months?.find((m) => m.month === monthKey),
    [report, monthKey]
  );

  const years = [];
  for (let y = currentYear - 3; y <= currentYear + 1; y++) years.push(y);

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
      <SectionHeader
        eyebrow="Financeiro"
        title="Relatórios"
        description="Acompanhe sua receita por mês e por forma de pagamento."
        action={
          <div className="flex gap-2 items-center">
            <Button asChild variant="outline" className="rounded-full border-[#E8E4D9]" data-testid="export-csv-year">
              <a href={financialCsvUrl({ year })}><Download className="w-4 h-4 mr-2" /> CSV ano</a>
            </Button>
            <Button asChild variant="outline" className="rounded-full border-[#E8E4D9]" data-testid="export-csv-month">
              <a href={financialCsvUrl({ month: monthKey })}><Download className="w-4 h-4 mr-2" /> CSV mês</a>
            </Button>
            <Button variant="outline" onClick={() => window.print()} className="rounded-full border-[#E8E4D9]" data-testid="print-pdf">
              <Printer className="w-4 h-4 mr-2" /> Imprimir
            </Button>
            <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
              <SelectTrigger className="rounded-full bg-white border-[#E8E4D9] w-32" data-testid="year-select"><SelectValue /></SelectTrigger>
              <SelectContent>{years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        }
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <Stat testId="year-total" label={`Receita ${year}`} value={formatBRL(report?.year_total)} accent />
        <Stat testId="month-pix" label="Pix no mês" value={formatBRL(selectedMonth?.by_method?.pix)} />
        <Stat testId="month-dinheiro" label="Dinheiro no mês" value={formatBRL(selectedMonth?.by_method?.dinheiro)} />
        <Stat testId="month-cartao" label="Cartão no mês" value={formatBRL(selectedMonth?.by_method?.cartao)} />
      </div>

      <div className="elegant-card p-8 mb-8">
        <h2 className="font-serif-display text-2xl text-[#2E332C] mb-6">Receita mensal · {year}</h2>
        <div style={{ width: "100%", height: 288, minHeight: 240 }} data-testid="revenue-chart">
          <ResponsiveContainer width="100%" height="100%" minWidth={0}>
            <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E8E4D9" vertical={false} />
              <XAxis dataKey="name" stroke="#5C6658" fontSize={12} tickLine={false} axisLine={false} />
              <YAxis stroke="#5C6658" fontSize={12} tickLine={false} axisLine={false} tickFormatter={(v) => `R$${v}`} />
              <Tooltip
                cursor={{ fill: "#F1EEE3" }}
                contentStyle={{ background: "#FFFFFF", border: "1px solid #E8E4D9", borderRadius: 12 }}
                formatter={(v) => formatBRL(v)}
              />
              <Bar dataKey="total" fill="#7B8B6F" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="elegant-card p-8">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <h2 className="font-serif-display text-2xl text-[#2E332C]">Lançamentos do mês</h2>
          <Select value={monthKey} onValueChange={setMonthKey}>
            <SelectTrigger className="rounded-full bg-white border-[#E8E4D9] w-44" data-testid="month-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {(report?.months || []).map((m) => (
                <SelectItem key={m.month} value={m.month}>{monthName(m.month_num)} / {year}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {monthTattoos.length === 0 ? (
          <p className="text-sm text-[#5C6658]">Nenhum lançamento neste mês.</p>
        ) : (
          <div className="overflow-x-auto" data-testid="month-tattoos-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left border-b border-[#E8E4D9]">
                  <th className="py-3 text-[10px] uppercase tracking-wider text-[#5C6658] font-medium">Data</th>
                  <th className="py-3 text-[10px] uppercase tracking-wider text-[#5C6658] font-medium">Cliente</th>
                  <th className="py-3 text-[10px] uppercase tracking-wider text-[#5C6658] font-medium">Descrição</th>
                  <th className="py-3 text-[10px] uppercase tracking-wider text-[#5C6658] font-medium">Método</th>
                  <th className="py-3 text-[10px] uppercase tracking-wider text-[#5C6658] font-medium">Status</th>
                  <th className="py-3 text-[10px] uppercase tracking-wider text-[#5C6658] font-medium text-right">Sinal</th>
                  <th className="py-3 text-[10px] uppercase tracking-wider text-[#5C6658] font-medium text-right">Valor</th>
                </tr>
              </thead>
              <tbody>
                {monthTattoos.map((t) => (
                  <tr key={t.id} className="border-b border-[#F1EEE3]">
                    <td className="py-4 text-[#5C6658]">{formatDate(t.date)}</td>
                    <td className="py-4 text-[#2E332C]">{t.client_name}</td>
                    <td className="py-4 text-[#5C6658]">{t.description}</td>
                    <td className="py-4 text-[#5C6658]">{paymentLabel(t.payment_method)}</td>
                    <td className="py-4"><span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-full border ${statusBadge(t.payment_status)}`}>{statusLabel(t.payment_status)}</span></td>
                    <td className="py-4 text-[#7B8B6F] text-right">{t.sinal ? formatBRL(t.sinal) : "—"}</td>
                    <td className="py-4 font-medium text-[#2E332C] text-right">{formatBRL(t.price)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </motion.div>
  );
}
